import React, { useState } from 'react'
import { Table, Tag, Input, Button, Radio, Checkbox } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import _ from 'lodash'
import './index.sass'
import {
  getEventTagClass,
  formatEventType,
  eventFilterOptions
} from './eventTypes'

export default function GlobalTimelineTable(props) {
  const {
    dataSource,
    loading,
    onFiltersChange,
    filters,
    setFilters,
    onRowClick
  } = props

  const [searchClient, setSearchClient] = useState('')
  const [searchEvent, setSearchEvent] = useState('')

  const columns = [
    {
      title: 'Client Name',
      key: 'client_name',
      dataIndex: 'client_name',
      width: 200,
      filteredValue: filters.client_name || null,
      filterDropdown: (filterProps) => (
        <SearchFilterDropDown
          {...filterProps}
          placeholder="Search clients"
          searchCallback={(searchText) => setSearchClient(searchText)}
        />
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text, record) => (
        <a
          className="client-link"
          onClick={() => onRowClick && onRowClick(record)}
        >
          {searchClient ? (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={getSearchWords(searchClient)}
              autoEscape
              textToHighlight={text.toString()}
            />
          ) : (
            text
          )}
        </a>
      )
    },
    {
      title: 'Event Type',
      key: 'event_type',
      dataIndex: 'event_type',
      width: 180,
      filters: eventFilterOptions,
      filteredValue: filters.event_type || null,
      filterDropdown: (filterProps) => (
        <CustomFilterDropdown
          {...filterProps}
          items={eventFilterOptions}
          height={250}
        />
      ),
      // Note: No onFilter function - filtering is done server-side by the API
      render: (text) => (
        <span className={`event-type-tag ${getEventTagClass(text)}`}>
          {formatEventType(text)}
        </span>
      )
    },
    {
      title: 'Event Name',
      key: 'event_name',
      dataIndex: 'event_name',
      width: 200,
      render: (text) => (
        searchEvent ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={getSearchWords(searchEvent)}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
          />
        ) : (
          text || '-'
        )
      )

    },
    {
      title: 'Planned Date',
      key: 'planned_date',
      dataIndex: 'planned_date',
      width: 150,
      render: (text) => text && moment(text).format('MMM DD, YYYY')
    },
    {
      title: 'Completed Date',
      key: 'completed_date',
      dataIndex: 'completed_date',
      width: 150,
      render: (text) => text && moment(text).format('MMM DD, YYYY')
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'completed_date',
      width: 100,
      filteredValue: filters.status || [],
      filterDropdown: (filterProps) => (
        <StatusFilterDropdown {...filterProps} />
      ),
      onFilter: (value, record) => {
        const isCancelled = record.status === 'cancelled'
        if (value === 'cancelled') return isCancelled
        const isCompleted = record.completed_date !== null && record.completed_date !== undefined && record.completed_date !== ''
        if (value === 'completed') return isCompleted && !isCancelled
        if (value === 'pending') return !isCompleted && !isCancelled
        return true
      },
      render: (completedDate, record) => {
        const isCancelled = record && record.status === 'cancelled'
        if (isCancelled) {
          return <Tag color="red">Cancelled</Tag>
        }
        const isCompleted = completedDate !== null && completedDate !== undefined && completedDate !== ''
        return (
          <Tag color={isCompleted ? 'green' : 'orange'}>
            {isCompleted ? 'Completed' : 'Pending'}
          </Tag>
        )
      }
    }
  ]

  return (
    <div className="table-container">
      <Table
        columns={columns}
        dataSource={dataSource || []}
        rowKey="id"
        loading={loading}
        pagination={false}
        onChange={(pagination, tableFilters) => {
          if (tableFilters) {
            const cleanedFilters = {}
            Object.keys(tableFilters).forEach(key => {
              if (tableFilters[key] && tableFilters[key].length > 0) {
                cleanedFilters[key] = tableFilters[key]
              }
            })
            setFilters(cleanedFilters)
            onFiltersChange && onFiltersChange(cleanedFilters)
          }
        }}
        size="middle"
      />
    </div>
  )

  function getSearchWords(text) {
    return text ? text.split(',').map(item => item.trim()) : []
  }
}

function CustomFilterDropdown({
  items,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  labelKey = 'label',
  valueKey = 'value',
  height = 200
}) {
  const isAllSelected = selectedKeys.length === items.length

  return (
    <div className='table-filter-container' style={{ height, minWidth: '180px' }}>
      <div className='filter-scrollable-area'>
        <Checkbox
          indeterminate={selectedKeys.length > 0 && selectedKeys.length < items.length}
          checked={isAllSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedKeys(items.map(item => item[valueKey]))
            } else {
              setSelectedKeys([])
            }
          }}
          className='select-all-option'
          style={{ marginBottom: '8px', fontWeight: 'bold' }}
        >
          Select All
        </Checkbox>
        {items.map((item) => (
          <div key={item[valueKey]} className='filter-values' style={{ marginBottom: '4px' }}>
            <Checkbox
              checked={selectedKeys.includes(item[valueKey])}
              onChange={() => {
                const value = item[valueKey]
                if (selectedKeys.includes(value)) {
                  setSelectedKeys(selectedKeys.filter(key => key !== value))
                } else {
                  setSelectedKeys([...selectedKeys, value])
                }
              }}
            >
              {item[labelKey]}
            </Checkbox>
          </div>
        ))}
      </div>

      <div className='filter-footer' style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderTop: '1px solid #f0f0f0' }}>
        <Button
          onClick={() => {
            clearFilters()
            setSelectedKeys([])
            confirm()
          }}
          size="small"
          className='reset-button'
          disabled={selectedKeys.length === 0}
          style={{ width: '48%', border: 'none' }}
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={() => {
            confirm()
          }}
          size="small"
          className='submit-button'
          style={{ width: '40%' }}
        >
          OK
        </Button>
      </div>
    </div>
  )
}

function StatusFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters
}) {
  const statusOptions = [
    { label: 'Completed', value: 'completed' },
    { label: 'Pending', value: 'pending' },
    { label: 'Cancelled', value: 'cancelled' }
  ]

  const selectedValue = selectedKeys.length > 0 ? selectedKeys[0] : null

  const handleChange = (e) => {
    setSelectedKeys([e.target.value])
  }

  const handleApply = () => {
    confirm()
  }

  const handleReset = () => {
    setSelectedKeys([])
    clearFilters()
    confirm()
  }

  return (
    <div style={{ padding: '8px' }}>
      <Radio.Group
        value={selectedValue}
        onChange={handleChange}
        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
      >
        {statusOptions.map((option) => (
          <Radio key={option.value} value={option.value}>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        <Button
          type="primary"
          size="small"
          onClick={handleApply}
        >
          OK
        </Button>
        <Button
          size="small"
          onClick={handleReset}
          disabled={selectedKeys.length === 0}
        >
          Reset
        </Button>
      </div>
    </div>
  )
}

function SearchFilterDropDown(props) {
  const {
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    searchCallback,
    placeholder = 'Search'
  } = props

  return (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={handleSearch}
        style={{ width: 188, marginBottom: 8, display: 'block' }}
      />
      <Button
        type="primary"
        onClick={handleSearch}
        icon={<SearchOutlined />}
        size="small"
        style={{ width: 90, marginRight: 8 }}
      >
        Search
      </Button>
      <Button
        onClick={handleReset}
        size="small"
        style={{ width: 90 }}
      >
        Reset
      </Button>
    </div>
  )

  function handleSearch() {
    confirm()
    searchCallback && searchCallback(selectedKeys[0])
  }

  function handleReset() {
    setSelectedKeys([])
    clearFilters()
    confirm()
    searchCallback && searchCallback('')
  }
}
